import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, Button, Select, MenuItem,
    FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Chip, Stack, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
    Checkbox, ListItemText
} from '@mui/material';
import axios from 'axios';
import Navbar from '../setup/Navbar';
import { Print as PrintIcon, Download as DownloadIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import '../../services/GlobalVariables';


const THERMAL_WIDTH = 42;
const thermalClamp = (value, max = THERMAL_WIDTH) => {
    const text = String(value ?? '');
    return text.length <= max ? text : text.slice(0, Math.max(0, max - 3)) + '...';
};
const thermalCenter = (value, width = THERMAL_WIDTH) => {
    const text = thermalClamp(value, width);
    return ' '.repeat(Math.floor(Math.max(0, width - text.length) / 2)) + text;
};
const thermalLeftRight = (left, right, width = THERMAL_WIDTH) => {
    const rightText = thermalClamp(right, width);
    const leftText = thermalClamp(left, Math.max(1, width - rightText.length - 1));
    return leftText + ' '.repeat(Math.max(1, width - leftText.length - rightText.length)) + rightText;
};

/**
 * Item Changes Audit Report
 * Shows complete audit trail of item quantity changes (increases, decreases, cancellations)
 * Provides summary statistics and filtering options
 */

export default function ItemChangesAuditReport() {
    const [changes, setChanges] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
    const [savingMatchId, setSavingMatchId] = useState(null);
    
    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [changeTypes, setChangeTypes] = useState([]);
    const [limit, setLimit] = useState(50);
    const [offset, setOffset] = useState(0);
    
    // Fetch summary on component mount
    useEffect(() => {
        fetchSummary();
    }, []);
    
    const fetchSummary = async () => {
        try {
            const params = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            
            const response = await axios.get(`${global.SET_API_URL}itemChangesAudit/getSummary`, { params });
            if (response.data.success) {
                setSummary(response.data.summary);
            }
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };
    
    const fetchChanges = async () => {
        setLoading(true);
        try {
            const params = {
                limit,
                offset
            };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (changeTypes.length > 0) params.changeType = changeTypes.join(',');
            
            const response = await axios.get(`${global.SET_API_URL}itemChangesAudit/getAllChanges`, { params });
            if (response.data.success) {
                setChanges(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching changes:', error);
            alert('Error loading changes data');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSearch = () => {
        setOffset(0);
        fetchChanges();
        fetchSummary();
    };
    
    const getChangeTypeColor = (type) => {
        switch(type) {
            case 'INCREASE': return 'success';
            case 'DECREASE': return 'warning';
            case 'CANCEL': return 'error';
            default: return 'default';
        }
    };
    

    const thermalPreviewText = React.useMemo(() => {
        const lines = [], divider = '='.repeat(THERMAL_WIDTH);
        lines.push(thermalCenter('Lucky Braost'), thermalCenter('Item Changes Audit'), divider);
        lines.push(thermalLeftRight('Printed', new Date().toLocaleString('en-PK')));
        if (startDate) lines.push(thermalLeftRight('From', startDate));
        if (endDate) lines.push(thermalLeftRight('To', endDate));
        lines.push(thermalLeftRight('Type', changeTypes.length > 0 ? changeTypes.join(', ') : 'ALL'), divider);
        (summary?.byType || []).forEach((stat) => {
            lines.push(thermalLeftRight(stat.changeType, `${stat.changeCount} / Qty ${stat.totalQuantity}`));
            if (stat.changeType !== 'INCREASE') lines.push(thermalLeftRight('  Waste', stat.totalWaste || 0));
        });
        lines.push(divider);
        changes.forEach((change, index) => {
            lines.push(thermalLeftRight(`#${index + 1} Order ${change.orderId}`, change.changeType));
            lines.push(thermalLeftRight('KOT No', change.kotNo || 'N/A'));
            lines.push(thermalClamp(change.itemName || 'Unknown Item'));
            lines.push(thermalLeftRight('Qty', `${change.oldQuantity} -> ${change.newQuantity}`));
            lines.push(thermalLeftRight('Change / Waste', `${change.changeAmount} / ${change.wasteQuantity || 0}`));
            lines.push(thermalLeftRight('User', change.dispName || change.userName || 'N/A'));
            if (change.reason) lines.push(thermalClamp(`Reason: ${change.reason}`));
            lines.push('-'.repeat(THERMAL_WIDTH));
        });
        lines.push(thermalLeftRight('Records', changes.length), thermalCenter('END OF REPORT'));
        return lines.join('\n');
    }, [changes, summary, startDate, endDate, changeTypes]);

    const handlePrintThermal = () => {
        const popup = window.open('', '_blank', 'width=420,height=760');
        if (!popup) return alert('Please allow popups to print the report');
        const escapedText = thermalPreviewText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        popup.document.write(`<html><head><title>Item Changes Audit Thermal Print</title>
        <style>@page { size: 80mm auto; margin: 4mm; } body { margin:0; font-family:'Courier New',monospace; }
        .sheet { width:72mm; margin:0 auto; padding:4mm 0; } pre { margin:0; white-space:pre-wrap; font-size:11px; line-height:1.35; }</style>
        </head><body><div class="sheet"><pre>${escapedText}</pre></div>
        <script>window.onload=function(){window.focus();window.print();};</script></body></html>`);
        popup.document.close();
    };

    const handleMatchedChange = async (change, checked) => {
        setSavingMatchId(change.id);
        setChanges((current) => current.map((row) => row.id === change.id ? { ...row, isMatched: checked ? 1 : 0 } : row));
        try {
            await axios.patch(`${global.SET_API_URL}itemChangesAudit/${change.id}/matched`, { isMatched: checked });
        } catch (error) {
            setChanges((current) => current.map((row) => row.id === change.id ? { ...row, isMatched: change.isMatched } : row));
            console.error('Error updating matched status:', error);
            alert('Matched status save nahi ho saka. Dobara try karein.');
        } finally {
            setSavingMatchId(null);
        }
    };

    const handleDownloadThermalPdf = () => {
        const lines = thermalPreviewText.split('\n'), lineHeight = 4;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [80, Math.max(80, 12 + lines.length * lineHeight)] });
        doc.setFont('courier', 'normal'); doc.setFontSize(8);
        lines.forEach((line, index) => doc.text(line, 4, 7 + index * lineHeight));
        doc.save(`Item_Changes_Audit_${startDate || 'all'}_${endDate || 'all'}.pdf`);
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                    📊 Item Changes Audit Report
                </Typography>
                
                {/* Summary Cards */}
                {summary && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {summary.byType.map((stat) => (
                            <Grid item xs={12} md={4} key={stat.changeType}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" color="text.secondary">
                                            {stat.changeType}
                                        </Typography>
                                        <Typography variant="h4">
                                            {stat.changeCount}
                                        </Typography>
                                        <Typography variant="body2">
                                            Total Quantity: {stat.totalQuantity}
                                        </Typography>
                                        {stat.changeType !== 'INCREASE' && (
                                            <Typography variant="body2" color="error">
                                                Waste: {stat.totalWaste} pcs
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
                
                {/* Top Changed Items */}
                {summary && summary.topChangedItems.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                🔥 Most Changed Items
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item Name</TableCell>
                                            <TableCell align="right">Changes</TableCell>
                                            <TableCell align="right">Increased</TableCell>
                                            <TableCell align="right">Decreased</TableCell>
                                            <TableCell align="right">Cancelled</TableCell>
                                            <TableCell align="right">Total Waste</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {summary.topChangedItems.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.itemName}</TableCell>
                                                <TableCell align="right">{item.changeCount}</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                                    +{item.totalIncreased}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'warning.main' }}>
                                                    -{item.totalDecreased}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                                    ✕{item.totalCancelled}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                                                    {item.totalWaste}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}
                
                {/* Filters */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            🔍 Filters
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Change Type</InputLabel>
                                    <Select
                                        multiple
                                        value={changeTypes}
                                        label="Change Type"
                                        onChange={(e) => setChangeTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                        renderValue={(selected) => selected.length > 0 ? selected.join(', ') : 'All'}
                                    >
                                        {['INCREASE', 'DECREASE', 'CANCEL'].map((type) => (
                                            <MenuItem key={type} value={type}>
                                                <Checkbox checked={changeTypes.includes(type)} />
                                                <ListItemText primary={type.charAt(0) + type.slice(1).toLowerCase()} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Stack direction="row" spacing={1}>
                                    <Button 
                                        variant="contained" 
                                        onClick={handleSearch}
                                        disabled={loading}
                                        fullWidth
                                    >
                                        {loading ? 'Loading...' : 'Search'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                
                {/* Changes Table */}
                <Card>
                    <CardContent>
<Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                            <Typography variant="h6">Change History</Typography>
                            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => setPrintPreviewOpen(true)} disabled={!changes.length}>
                                Print / PDF
                            </Button>
                        </Stack>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding={'checkbox'}>Matched</TableCell>
                                        <TableCell>Date/Time</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Order ID</TableCell>
                                        <TableCell>KOT No</TableCell>
                                        <TableCell>Item Name</TableCell>
                                        <TableCell align="center">Type</TableCell>
                                        <TableCell align="right">Old Qty</TableCell>
                                        <TableCell align="right">New Qty</TableCell>
                                        <TableCell align="right">Change</TableCell>
                                        <TableCell align="right">KOT Printed</TableCell>
                                        <TableCell align="right">Waste</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {changes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center">
                                                No changes found. Click Search to load data.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        changes.map((change) => (
                                            <TableRow key={change.id}>
                                                <TableCell padding={'checkbox'}>
                                                    <Checkbox
                                                        checked={Boolean(Number(change.isMatched))}
                                                        disabled={savingMatchId === change.id}
                                                        onChange={(event) => handleMatchedChange(change, event.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(change.createdAt).toLocaleString()}
                                                </TableCell>
                                                <TableCell>{change.dispName || change.userName || 'N/A'}</TableCell>
                                                <TableCell>{change.orderId}</TableCell>
                                                <TableCell>{change.kotNo || '-'}</TableCell>
                                                <TableCell>{change.itemName}</TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={change.changeType} 
                                                        color={getChangeTypeColor(change.changeType)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{change.oldQuantity}</TableCell>
                                                <TableCell align="right">{change.newQuantity}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {change.changeType === 'INCREASE' ? '+' : '-'}
                                                    {change.changeAmount}
                                                </TableCell>
                                                <TableCell align="right">{change.kotPrintedQuantity}</TableCell>
                                                <TableCell align="right" sx={{ color: change.wasteQuantity > 0 ? 'error.main' : 'inherit' }}>
                                                    {change.wasteQuantity}
                                                </TableCell>
                                                <TableCell>{change.reason || '-'}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        {/* Pagination */}
                        {changes.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">
                                    Showing {offset + 1} to {offset + changes.length} entries
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => {
                                            setOffset(Math.max(0, offset - limit));
                                            fetchChanges();
                                        }}
                                        disabled={offset === 0}
                                    >
                                        Previous
                                    </Button>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => {
                                            setOffset(offset + limit);
                                            fetchChanges();
                                        }}
                                        disabled={changes.length < limit}
                                    >
                                        Next
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>

            <Dialog open={printPreviewOpen} onClose={() => setPrintPreviewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Item Changes Audit - Thermal Preview</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Current filters ke loaded records ka 80mm thermal preview.</Typography>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: '60vh', overflow: 'auto' }}>
                        <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: '"Courier New", monospace', fontSize: 12, lineHeight: 1.4 }}>{thermalPreviewText}</Box>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPrintPreviewOpen(false)}>Close</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadThermalPdf}>Download PDF</Button>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintThermal}>Print 80mm</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
